import React, { useState } from "react";
import OtpModal from "./OtpModal";
import TextInput from "./shared/TextInput";
import DateInput from "./shared/DateInput";
import RadioGroup from "./shared/RadioGroup";
import SelectInput from "./shared/SelectInput";
import NumberInput from "./shared/NumberInput";
import NotificationModal from "./NotificationModal";
import { PostRequestOtp, PostTradinessData, PostVerifyOtp } from "../apis/post";
import AcknowledgeComponent from "./AcknowledgeComponent";
import {
  formatEstDate,
  getEstDateTime,
  getLocalItem,
  setLocalItem,
} from "../../utilities/util";
import { host } from "../../services/api";

interface TardinessFormData {
  employeeName: string;
  dateOfRequest: string;
  restaurantLocation: string;
  selectedDate: string;
  selectedTime: string | null;
  reason: string;
  mobileNumber: string;
  countryCode: string;
  acknowledge: boolean;
}

const REASON_OPTIONS = [
  { id: "1", text: "Were late for your scheduled work shift" },
  { id: "2", text: "Did not come to work" },
  { id: "3", text: "Did not come to work and will be using NYS Sick Pay" },
];

const TardinessForm: React.FC = () => {
  const [form, setForm] = useState<TardinessFormData>({
    employeeName: "",
    dateOfRequest: formatEstDate(getEstDateTime()),
    restaurantLocation: "",
    selectedDate: "",
    selectedTime: null,
    reason: "",
    mobileNumber: "",
    countryCode: "+1",
    acknowledge: false,
  });

  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [showNotice, setShowNotice] = useState({ state: false, message: "" });

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, type, value } = e.target;
    const isCheckbox = type === "checkbox";

    let finalValue: string | boolean;

    if (isCheckbox) {
      finalValue = (e.target as HTMLInputElement).checked;
    } else if (e.target instanceof HTMLSelectElement) {
      const selectedText = e.target.options[e.target.selectedIndex].innerText;
      finalValue = selectedText; // use text instead of value
    } else {
      finalValue = value;
    }

    if (type === "date" && value) {
      const formattedDate = new Date(value).toISOString().split('T')[0]; // Format as 'YYYY-MM-DD'
      finalValue = formattedDate;
    }

    setForm((prev) => ({
      ...prev,
      [name]: finalValue,
    }));
  };
  const showTemporaryNotice = (object: any) => {
    setShowNotice(object);
    setTimeout(() => {
      setShowNotice({ state: false, message: "" });
    }, 3000);
  };

  const handleSendOtp = async () => {
    if (!form.employeeName.trim()) {
      showTemporaryNotice({
        state: true,
        message: "Employee Name is required!",
      });
      return;
    }
    if (!form.dateOfRequest.trim()) {
      showTemporaryNotice({
        state: true,
        message: "Date of Request is required!",
      });
      return;
    }
    if (!form.restaurantLocation.trim()) {
      showTemporaryNotice({
        state: true,
        message: "Restaurant Location is required!",
      });
      return;
    }

    if (!form.reason.trim()) {
      showTemporaryNotice({ state: true, message: "Reason is required!" });
      return;
    }
    if (!form.selectedDate.trim()) {
      showTemporaryNotice({ state: true, message: "Date is required!" });
      return;
    }
    if (form.mobileNumber.trim().length !== 10) {
      showTemporaryNotice({
        state: true,
        message: "Please enter a valid 10-digit mobile number.",
      });
      return;
    }

    const _res = await PostRequestOtp({
      phone: form.countryCode + form.mobileNumber,
      type: "t",
    });
    if (_res.status) {
      showTemporaryNotice({ state: true, message: "OTP sent successfully!" });
      setOtpSent(true);
      setShowOtpModal(true);
    } else {
      showTemporaryNotice({ state: true, message: _res.message ?? _res });
    }
  };

  const handleOtpVerified = async (otp: string) => {
    const _res = await PostVerifyOtp({
      phone: form.countryCode + form.mobileNumber,
      otp: otp,
    });
    if (_res.status) {
      setOtpVerified(true);
      setShowOtpModal(false);
      showTemporaryNotice({ state: true, message: "OTP Verified!" });
    } else {
      showTemporaryNotice({ state: true, message: "Invalid OTP." });
    }
  };

  const downloadPdf = async () => {
    const id: string = getLocalItem("tardinessPdfId")?.toString() || "";
    const type: string = "t";
    const query = new URLSearchParams({ id, type });
    const apiUrl = host + "admin/pdf?" + query;
    try {
      // Fetch the PDF from the API
      const response = await fetch(apiUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/pdf",
        },
      });

      // Check if the response is OK
      if (!response.ok) {
        throw new Error("Failed to fetch the PDF");
      }

      // Get the PDF blob
      const pdfBlob = await response.blob();

      // Create a link element
      const downloadLink = document.createElement("a");

      // Create an object URL for the blob and set it as the href for the link
      const url = window.URL.createObjectURL(pdfBlob);
      downloadLink.href = url;

      // Set the filename for the downloaded PDF
      downloadLink.download = "generated.pdf";

      // Programmatically click the link to trigger the download
      downloadLink.click();

      // Optionally, revoke the object URL to free memory
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading PDF:", error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!otpVerified) {
      showTemporaryNotice({
        state: true,
        message: "Please verify OTP before submitting.",
      });
      return;
    }

    console.log("Tardiness is form submitted:", form);
    const res = await PostTradinessData(form);
    console.log("API RESPONSE", res);
    if (res.status) {
      showTemporaryNotice({ state: true, message: res.message });
      setForm({
        employeeName: "",
        dateOfRequest: formatEstDate(getEstDateTime()),
        restaurantLocation: "",
        selectedDate: "",
        selectedTime: null,
        reason: "",
        mobileNumber: "",
        countryCode: "+1",
        acknowledge: false,
      });
      setOtpVerified(false);
      setOtpSent(false);
      setLocalItem("tardinessPdfId", res?.id);
      await downloadPdf();
    } else {
      showTemporaryNotice({ state: true, message: res.message ?? res });
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-6 max-w-6xl mx-auto p-6 bg-black bg-opacity-40 backdrop-blur rounded-2xl shadow-2xl text-white mt-8 mb-8"
    >
      <h2 className="text-xl font-bold text-yellow-300 flex justify-center gap-2">
        <div>
          {/* <i className="fas fa-clock text-yellow-400"></i> */}
          {"New York City - Notice of Tardiness Or Absenteeism Notice".toUpperCase()}
        </div>
      </h2>

      <div className="grid md:grid-cols-2 gap-4">
        <TextInput
          label="Employee Name (As on the schedule)"
          name="employeeName"
          value={form.employeeName}
          onChange={handleChange}
          placeholder="Employee Name"
          required
        />
        <DateInput
          label="Date of Request"
          name="dateOfRequest"
          value={form.dateOfRequest}
          onChange={handleChange}
          required
          readonly
        />
        <SelectInput
          label="Restaurant Location"
          name="restaurantLocation"
          value={form.restaurantLocation}
          onChange={handleChange}
          required
          options={[
            { value: "2549 - Shore Pkwy", label: "2549 - Shore Pkwy" },
            { value: "105355 - 86 Street", label: "105355 - 86 Street" },
          ]}
        />
      </div>

      <RadioGroup
        label="Reason"
        name="reason"
        selectedValue={form.reason}
        options={REASON_OPTIONS}
        onChange={handleChange}
        required
      />
      {form.reason && (
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <DateInput
            label="Select Date"
            name="selectedDate"
            value={form.selectedDate || ""}
            onChange={handleChange}
            required
          />
          {/* <TimeInput
                        label="Select Time"
                        name="selectedTime"
                        value={form.selectedTime || ""}
                        onChange={handleChange}
                        required
                    /> */}
        </div>
      )}

      <AcknowledgeComponent
        acknowledge={form.acknowledge}
        onAcknowledge={(value) => setForm({ ...form, acknowledge: value })}
      />

      {form.acknowledge && (
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mt-4">
          <div className="flex flex-col sm:flex-row items-center gap-4 w-full sm:w-auto">
            <div className="flex items-center gap-2 w-full sm:w-auto">
              {/* <CountryCodeSelect onChange={(code) => setForm({ ...form, countryCode: code })} /> */}

              <NumberInput
                label=""
                name="mobileNumber"
                value={form.mobileNumber}
                onChange={handleChange}
                placeholder="10-digit mobile number"
                minLength={10}
                className="w-full sm:w-auto border-t border-l border-gray-400 rounded-r-md focus:ring-2 focus:ring-yellow-500"
              />
            </div>

            <button
              type="button"
              onClick={handleSendOtp}
              className="bg-yellow-500 flex-none text-black font-bold px-4 py-2 rounded-lg shadow hover:bg-yellow-600 transition"
            >
              {otpSent ? "Resend OTP" : "Send OTP"}
            </button>
          </div>
          <button
            type="submit"
            className={`px-6 py-2  rounded-lg font-bold transition ${otpVerified
                ? "bg-green-500 text-black hover:bg-green-600"
                : "bg-gray-500 text-gray-300 cursor-not-allowed"
              }`}
            disabled={!otpVerified}
          >
            Submit Tardiness Notice
          </button>
        </div>
      )}

      <OtpModal
        show={showOtpModal}
        phone={form.mobileNumber}
        onClose={() => setShowOtpModal(false)}
        onVerify={handleOtpVerified}
        onResend={() => alert("Resend triggered")}
      />

      <NotificationModal
        show={showNotice.state}
        onClose={() => setShowNotice({ state: false, message: "" })}
        message={showNotice.message}
      />
    </form>
  );
};

export default TardinessForm;
